import { useContext, useEffect, useState } from 'react';
import { setIsLoggingIn, setUser } from '@/features/user-session';
import log from '@/lib/logger';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { useMsal } from '@azure/msal-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, List, ListItem, ListItemText, Popover } from '@mui/material';
import { Link, useNavigate } from '@tanstack/react-router';
import NotificationsQuickView from 'components/Notifications/NotificationsQuickView';
import { http } from 'config';
import { resetStore, useAppDispatch, useAppSelector } from 'features';
import { addNotification } from 'features/Notifications';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Nprogress from 'nprogress';
import { userSignOut } from 'services/accounts';
import { toast } from 'sonner';
import { type WebsocketNotification } from 'types/client';
import { type Notification, type User } from 'types/swagger';

const getUserInitials = (user: User) =>
  user.first_name && user.last_name
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
    : user.first_name
      ? user.first_name.charAt(0)
      : user?.email?.charAt(0);

const SettingsNotificationsButton = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance: msalInstance } = useMsal();
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const { user, setShouldRedirect, notificationsWebsocket } = useContext(UserSessionContext);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const unseenNotifications = useAppSelector((state) => state.notifications.unseenCount);
  useDocumentTitle(
    unseenNotifications ? `(${unseenNotifications}) Enerlink: CMS` : 'Enerlink: CMS'
  );
  useEffect(() => {
    if (notificationsWebsocket) {
      const newWebsocket: WebSocket = notificationsWebsocket;
      newWebsocket.onmessage = (event) => {
        const json = JSON.parse(event?.data ? event.data : undefined);
        if (json && json.type === 'authorization' && json.message === false) {
          newWebsocket.close();
        }
        if (json?.message?.data && json.message.message) {
          const receivedNotification: WebsocketNotification = json.message.data;
          const newNotification: Notification = {
            id: receivedNotification.notification,
            method: 'IN_APP',
            data: JSON.stringify(receivedNotification),
            title: '',
            action_code: receivedNotification.action_code,
            message: json.message.message,
            created: new Date().toISOString(),
            updated: new Date().toISOString(),
            seen: false,
          };
          dispatch(addNotification(newNotification));
        }
      };
    }

    return () => {
      if (notificationsWebsocket) {
        const newWebsocket = notificationsWebsocket;
        newWebsocket.onmessage = () => {};
      }
    };
  }, [dispatch, notificationsWebsocket, notifications]);

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuOpen(!isMenuOpen);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuOpen(false);
  };

  const clearSession = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('currentInstance');
    http.defaults.headers.common.Authorization = undefined;
    http.defaults.headers.common['access-token'] = undefined;
    dispatch(setUser(null));
    setShouldRedirect(true);
    dispatch(resetStore());
  };

  const clearMsalCache = () => {
    try {
      void msalInstance.clearCache();
    } catch (error) {
      log.error('Error al limpiar cache local de MSAL:', error);
    }
  };

  const handleSignOut = () => {
    Nprogress.start();
    dispatch(setIsLoggingIn(true));
    userSignOut(localStorage.getItem('refresh') || '')
      .catch(() => {})
      .finally(() => {
        clearSession();
        clearMsalCache();
        Nprogress.done();
        dispatch(setIsLoggingIn(false));
        void navigate({ to: '/', replace: true });
        toast.success('Sesión finalizada');
      });
  };
  return (
    user && (
      <div className="notifications-preferences-container mr-1 $tw-gap-1">
        <NotificationsQuickView />
        <Avatar
          sx={{
            color: 'whitesmoke',
            background: 'rgba(255,255,255,0.5)',
          }}
          alt={getUserInitials(user)?.toUpperCase()}
        >
          {getUserInitials(user)?.toUpperCase()}
        </Avatar>
        <Button
          size="icon"
          variant="ghost"
          className="$tw-relative $tw-rounded-full $tw-border-none hover:$tw-bg-muted/20"
          onClick={handleClickMenu}
        >
          <ExpandMoreIcon />
        </Button>
        <Popover
          open={isMenuOpen}
          anchorEl={menuAnchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPopover-paper': {
              width: 150,
              marginTop: 2,
            },
          }}
        >
          <List className="settings-menu-popover">
            <Link className="no-text-decoration" to="/preferences">
              <ListItem>
                <ListItemText sx={{ color: 'rgba(255,255,255,0.9)' }}>Preferencias</ListItemText>
              </ListItem>
            </Link>
            <Button
              variant="ghost"
              className="$tw-w-full $tw-rounded-none $tw-border-none $tw-bg-transparent $tw-p-0 $tw-normal-case"
              onClick={handleSignOut}
              asChild
            >
              <ListItem>
                <ListItemText sx={{ color: 'rgba(255,255,255,0.9)' }}>Cerrar sesión</ListItemText>
              </ListItem>
            </Button>
          </List>
        </Popover>
      </div>
    )
  );
};

export default SettingsNotificationsButton;
